// Pro Status Service - Fetch subscription/pro status from Firestore
import { db } from './firebase-config.js';
import { doc, getDoc } from 'firebase/firestore';

// Pro status types (matching extension)
export const ProType = {
    FREE: 'free',
    PROMO_LIFETIME: 'promo_lifetime',
    PROMO_TRIAL: 'promo_trial',
    SUBSCRIPTION_MONTHLY: 'subscription_monthly',
    SUBSCRIPTION_LIFETIME: 'subscription_lifetime'
};

/**
 * Get user's pro status from Firestore
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object>} Pro status object
 */
export async function getProStatus(userId) {
    if (!userId) {
        return { isPro: false, proType: ProType.FREE };
    }

    try {
        const proStatusRef = doc(db, 'users', userId, 'settings', 'proStatus');
        const docSnap = await getDoc(proStatusRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                isPro: data.isPro || false,
                proType: data.proType || ProType.FREE,
                expiresAt: data.expiresAt?.toDate() || null,
                promoCodeUsed: data.promoCodeUsed || null,
                subscriptionId: data.subscriptionId || null,
                activatedAt: data.activatedAt?.toDate() || null,
                updatedAt: data.updatedAt?.toDate() || null
            };
        }

        return { isPro: false, proType: ProType.FREE };
    } catch (error) {
        console.error('Error fetching pro status:', error);
        return { isPro: false, proType: ProType.FREE, error: error.message };
    }
}

/**
 * Get human-readable plan name
 * @param {string} proType - Pro type enum
 * @returns {string} Display name
 */
export function getPlanDisplayName(proType) {
    switch (proType) {
        case ProType.PROMO_LIFETIME:
            return 'Lifetime Live (Promo)';
        case ProType.PROMO_TRIAL:
            return 'Live Trial';
        case ProType.SUBSCRIPTION_MONTHLY:
            return 'Live Monthly';
        case ProType.SUBSCRIPTION_LIFETIME:
            return 'Lifetime Live';
        case ProType.FREE:
        default:
            return 'Free';
    }
}

/**
 * Check if subscription is expired
 * @param {Object} proStatus - Pro status object
 * @returns {boolean}
 */
export function isExpired(proStatus) {
    if (!proStatus.expiresAt) return false;
    return new Date() > proStatus.expiresAt;
}

/**
 * Get days until expiration
 * @param {Date} expiresAt - Expiration date
 * @returns {number} Days remaining (negative if expired)
 */
export function getDaysRemaining(expiresAt) {
    if (!expiresAt) return Infinity;
    const now = new Date();
    const diff = expiresAt - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
